import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill } from "@/components/common";
import FileUploader from "@/components/FileUploader";
import FormSection from "@/components/FormSection";
import { AutosaveIndicator, RestoreDraftBanner } from "@/components/AutosaveIndicator";
import useAutosave from "@/hooks/useAutosave";
import { Building2, Sparkles, FolderOpen } from "lucide-react";

const PROVINCES = ["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan", "Nova Scotia"];
const BUSINESS_TYPES = ["Distributor", "Manufacturer", "Reseller", "OEM", "Trading Company", "Other"];

export default function CompanyPage() {
  const { user, company, refresh } = useAuth();
  const [tab, setTab] = useState("basic");
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/meta/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => {
    if (company) setForm({
      legal_name: company.legal_name || "", address: company.address || "",
      city: company.city || "", province: company.province || "Ontario",
      postal_code: company.postal_code || "", phone: company.phone || "",
      tax_number: company.tax_number || "", categories: company.categories || [],
      warehouse_address: company.warehouse_address || "", bank_details: company.bank_details || "",
      service_regions: company.service_regions || [],
      subcategories: company.subcategories || [],
      certifications: company.certifications || [],
      brands: company.brands || [],
      years_in_business: company.years_in_business || "",
      team_size: company.team_size || "",
      business_type: company.business_type || "",
      lead_time_days_default: company.lead_time_days_default || "",
      moq: company.moq || "",
      about: company.about || "",
      website: company.website || "",
    });
  }, [company]);

  // Field-level errors (light — backend remains the authority).
  const errors = useMemo(() => {
    const e = {};
    if (!form) return e;
    if (!form.legal_name?.trim()) e.legal_name = "Required";
    if (!form.phone?.trim()) e.phone = "Required";
    if (!form.address?.trim()) e.address = "Required";
    if (!form.city?.trim()) e.city = "Required";
    if (form.website && form.website.trim() && !/^https?:\/\//i.test(form.website.trim())) {
      e.website = "Use http:// or https://";
    }
    if (form.about && form.about.length > 500) e.about = "Max 500 characters";
    return e;
  }, [form]);
  const hasErrors = Object.keys(errors).length > 0;

  // Autosave key bound to the company id so admins/owners can't cross-pollute drafts.
  const autosaveKey = company?.id ? `company-profile-${company.id}-v1` : null;
  const { restored, status, lastSavedAt, clear, dismissRestore } = useAutosave({
    storageKey: autosaveKey,
    data: form || {},
    enabled: !!form && !!autosaveKey,
    isEmpty: () => !form,
  });

  if (!form) return <div style={{ color: "var(--ink-mute)" }}>Loading…</div>;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setMsg(""); };
  const toggleArr = (k, val) => { setForm(f => ({ ...f, [k]: f[k].includes(val) ? f[k].filter(x => x !== val) : [...f[k], val] })); setMsg(""); };
  const arrFromCsv = (k) => (e) => {
    const list = e.target.value.split(",").map(x => x.trim()).filter(Boolean);
    set(k, list);
  };

  const resumeDraft = () => {
    if (restored?.data) setForm(f => ({ ...f, ...restored.data }));
    dismissRestore();
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    if (hasErrors) {
      setErr("Please fix the highlighted fields before saving.");
      return;
    }
    try {
      const payload = {
        ...form,
        years_in_business: form.years_in_business === "" ? null : Number(form.years_in_business),
        team_size: form.team_size === "" ? null : Number(form.team_size),
        lead_time_days_default: form.lead_time_days_default === "" ? null : Number(form.lead_time_days_default),
        moq: form.moq === "" ? null : Number(form.moq),
      };
      await api.put("/company", payload);
      await refresh();
      clear();
      setMsg("Profile saved. Your verification status is preserved — admin reviews any material changes.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const Tab = ({ id, icon: Icon, label, testId }) => (
    <button type="button" data-testid={testId} onClick={() => setTab(id)}
      className={`btn ${tab === id ? "btn-primary" : "btn-ghost"}`}
      style={{ padding: "8px 14px", fontSize: 13 }}>
      <Icon size={14} /> {label}
    </button>
  );

  const fieldErr = (k) => errors[k] && (
    <div className="field-error">{errors[k]}</div>
  );
  const inputCls = (k) => `input${errors[k] ? " input-error" : ""}`;

  return (
    <>
      <PageHeader
        title="Company Profile"
        subtitle={user.role === "supplier" ? "Complete your profile and capabilities to receive better-matched RFQs." : "Complete your profile for full RFQ access."}
        actions={<StatusPill status={company?.verification_status || "pending"} />}
      />

      <div className="card-tabs" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        <Tab id="basic" icon={Building2} label="Basic info" testId="tab-basic" />
        {user.role === "supplier" && <Tab id="caps" icon={Sparkles} label="Capabilities" testId="tab-caps" />}
        <Tab id="docs" icon={FolderOpen} label="Documents" testId="tab-docs" />
      </div>

      {(tab === "basic" || tab === "caps") && restored && (
        <div style={{ maxWidth: 880, marginBottom: 14 }}>
          <RestoreDraftBanner savedAt={restored.savedAt} onResume={resumeDraft} onDiscard={dismissRestore} />
        </div>
      )}

      {tab === "basic" && (
        <form onSubmit={submit} className="card" style={{ padding: "clamp(18px, 4vw, 28px)", maxWidth: 880, display: "grid", gap: 22 }} data-testid="company-basic-form">
          <div className="form-card-header">
            <span className="font-mono" style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--ink-mute)" }}>COMPANY · BASIC INFO</span>
            <AutosaveIndicator status={status} lastSavedAt={lastSavedAt} />
          </div>

          <FormSection index={1} title="Legal & contact" hint="Used for identity verification. Buyers only see this after award.">
            <div className="rfq-grid-2">
              <div className="field">
                <label className="label">Legal name *</label>
                <input className={inputCls("legal_name")} data-testid="co-name" required value={form.legal_name} onChange={e => set("legal_name", e.target.value)} />
                {fieldErr("legal_name")}
              </div>
              <div className="field">
                <label className="label">Phone *</label>
                <input className={inputCls("phone")} required value={form.phone} onChange={e => set("phone", e.target.value)} />
                {fieldErr("phone")}
              </div>
            </div>
            <div className="field">
              <label className="label">Tax / Business number</label>
              <input className="input" value={form.tax_number} onChange={e => set("tax_number", e.target.value)} placeholder="BN123456789" />
            </div>
          </FormSection>

          <FormSection index={2} title="Address" hint="Primary registered address. Add a separate warehouse below if applicable.">
            <div className="field">
              <label className="label">Address *</label>
              <input className={inputCls("address")} required value={form.address} onChange={e => set("address", e.target.value)} />
              {fieldErr("address")}
            </div>
            <div className="rfq-grid-3">
              <div className="field">
                <label className="label">City *</label>
                <input className={inputCls("city")} required value={form.city} onChange={e => set("city", e.target.value)} />
                {fieldErr("city")}
              </div>
              <div className="field">
                <label className="label">Province</label>
                <select className="input" value={form.province} onChange={e => set("province", e.target.value)}>
                  {PROVINCES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Postal code</label>
                <input className="input" value={form.postal_code} onChange={e => set("postal_code", e.target.value)} />
              </div>
            </div>
            {user.role === "supplier" && (
              <div className="rfq-grid-2">
                <div className="field">
                  <label className="label">Warehouse address</label>
                  <input className="input" value={form.warehouse_address} onChange={e => set("warehouse_address", e.target.value)} />
                </div>
                <div className="field">
                  <label className="label">Bank details (reference only)</label>
                  <input className="input" value={form.bank_details} onChange={e => set("bank_details", e.target.value)} placeholder="Bank ****1234" />
                  <div className="field-hint">Reference only — the platform never holds, escrows, or remits funds.</div>
                </div>
              </div>
            )}
          </FormSection>

          <FormSection index={3} title={user.role === "supplier" ? "Categories & regions you serve" : "Sourcing categories & regions"} hint={user.role === "supplier" ? "Drives RFQ matching. Pick everything you can quote on." : "Helps us route your RFQs to the right supplier pool."}>
            <div>
              <label className="label">{user.role === "supplier" ? "Categories supplied" : "Sourcing categories"}</label>
              <div className="chip-row">
                {cats.map(c => (
                  <button key={c} type="button" onClick={() => toggleArr("categories", c)} data-testid={`cat-${c}`} className={`btn ${form.categories.includes(c) ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="label">Service / delivery regions</label>
              <div className="chip-row">
                {PROVINCES.map(r => (
                  <button key={r} type="button" onClick={() => toggleArr("service_regions", r)} className={`btn ${form.service_regions.includes(r) ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>{r}</button>
                ))}
              </div>
            </div>
          </FormSection>

          {err && <div style={{ color: "var(--red)" }} data-testid="co-form-error">{err}</div>}
          {msg && <div style={{ color: "var(--emerald)" }} data-testid="co-form-msg">{msg}</div>}
          <div className="form-action-row">
            <button className="btn btn-primary" data-testid="save-company" disabled={hasErrors} title={hasErrors ? "Fix highlighted fields" : ""}>Save profile</button>
          </div>
        </form>
      )}

      {tab === "caps" && user.role === "supplier" && (
        <form onSubmit={submit} className="card" style={{ padding: "clamp(18px, 4vw, 28px)", maxWidth: 880, display: "grid", gap: 22 }} data-testid="company-caps-form">
          <div className="form-card-header">
            <div>
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)" }}>SUPPLIER CAPABILITIES</div>
              <p style={{ color: "var(--ink-dim)", fontSize: 13, marginTop: 4 }}>
                Visible to admin during verification. Helps us route the right RFQs to you. Buyers see your identity only after award.
              </p>
            </div>
            <AutosaveIndicator status={status} lastSavedAt={lastSavedAt} />
          </div>

          <FormSection index={1} title="About your operation" hint="A concise overview helps the admin verify your business and helps us match accurately.">
            <div className="field">
              <label className="label">Short description (max 500 characters)</label>
              <textarea
                className={`input${errors.about ? " input-error" : ""}`}
                data-testid="cap-about"
                maxLength={500}
                value={form.about}
                onChange={e => set("about", e.target.value)}
                style={{ minHeight: 90, resize: "vertical" }}
                placeholder="e.g. Tier-1 distributor of bearings & seals serving Ontario manufacturers since 2008."
              />
              <div className="field-hint">{form.about.length}/500</div>
              {fieldErr("about")}
            </div>
            <div className="rfq-grid-3">
              <div className="field">
                <label className="label">Business type</label>
                <select className="input" data-testid="cap-business-type" value={form.business_type} onChange={e => set("business_type", e.target.value)}>
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Years in business</label>
                <input className="input" data-testid="cap-years" type="number" min="0" value={form.years_in_business} onChange={e => set("years_in_business", e.target.value)} />
              </div>
              <div className="field">
                <label className="label">Team size</label>
                <input className="input" type="number" min="0" value={form.team_size} onChange={e => set("team_size", e.target.value)} />
              </div>
            </div>
          </FormSection>

          <FormSection index={2} title="Operations & defaults" hint="Defaults we use to pre-fill or sanity-check your quotes.">
            <div className="rfq-grid-3">
              <div className="field">
                <label className="label">Default lead time (days)</label>
                <input className="input" data-testid="cap-leadtime" type="number" min="0" value={form.lead_time_days_default} onChange={e => set("lead_time_days_default", e.target.value)} placeholder="e.g. 7" />
              </div>
              <div className="field">
                <label className="label">Minimum order qty (MOQ)</label>
                <input className="input" type="number" min="0" value={form.moq} onChange={e => set("moq", e.target.value)} placeholder="e.g. 1" />
              </div>
              <div className="field">
                <label className="label">Website</label>
                <input className={inputCls("website")} type="url" value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://" />
                {fieldErr("website")}
              </div>
            </div>
          </FormSection>

          <FormSection index={3} title="Specialities & brands" hint="The more accurate this is, the better your RFQ matches.">
            <div className="field">
              <label className="label">Certifications (comma-separated)</label>
              <input className="input" data-testid="cap-certifications" value={form.certifications.join(", ")} onChange={arrFromCsv("certifications")} placeholder="e.g. ISO 9001:2015, CSA Z299.3" />
              {form.certifications.length > 0 && (
                <div className="chip-row" style={{ marginTop: 8 }}>
                  {form.certifications.map(c => <span key={c} className="pill pill-quoted">{c}</span>)}
                </div>
              )}
            </div>
            <div className="field">
              <label className="label">Brands carried (comma-separated)</label>
              <input className="input" data-testid="cap-brands" value={form.brands.join(", ")} onChange={arrFromCsv("brands")} placeholder="e.g. SKF, Timken, NSK" />
              {form.brands.length > 0 && (
                <div className="chip-row" style={{ marginTop: 8 }}>
                  {form.brands.map(b => <span key={b} className="pill pill-quoted">{b}</span>)}
                </div>
              )}
            </div>
            <div className="field">
              <label className="label">Sub-specialities (comma-separated)</label>
              <input className="input" value={form.subcategories.join(", ")} onChange={arrFromCsv("subcategories")} placeholder="e.g. Spherical roller bearings, Mechanical seals, Pillow blocks" />
            </div>
          </FormSection>

          {err && <div style={{ color: "var(--red)" }} data-testid="cap-form-error">{err}</div>}
          {msg && <div style={{ color: "var(--emerald)" }} data-testid="cap-form-msg">{msg}</div>}
          <div className="form-action-row">
            <button className="btn btn-primary" data-testid="save-capabilities" disabled={hasErrors} title={hasErrors ? "Fix highlighted fields" : ""}>Save capabilities</button>
          </div>
        </form>
      )}

      {tab === "docs" && (
        <div style={{ maxWidth: 880 }}>
          <FileUploader purpose="verification_doc" relatedId={company?.id} label="Verification documents" />
          <div style={{ marginTop: 14, fontSize: 13, color: "var(--ink-dim)" }}>
            See all your company documents and version history in the <a href="/app/documents" data-testid="vault-link" style={{ color: "var(--amber-400)" }}>Document Vault →</a>
          </div>
        </div>
      )}
    </>
  );
}
